import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Orbit, ShieldCheck, Sparkles, ArrowUpRight } from "lucide-react";

const CALENDLY = "https://calendly.com/natalie-revolstudios";

const capabilities = [
  "360° Virtual Tours",
  "Avatar Creation",
  "Multilingual Dubbing",
  "Short-Form 360° Content",
  "Interactive Video",
  "Analytics & Performance",
];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const RevelleAvatarPanel = ({ status }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [8, -8]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-8, 8]);

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const statusLabel = status === "thinking" ? "Thinking" : status === "speaking" ? "Speaking" : "Ready";

  return (
    <motion.section
      ref={ref}
      onMouseMove={onMove}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="avatar-panel relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0c]/80 p-5 sm:p-8 shadow-2xl shadow-black/80 backdrop-blur-xl lg:min-h-[calc(100vh-180px)]"
      data-phase2="lip-sync-target"
      data-testid="avatar-panel"
    >
      <canvas className="avatar-canvas" style={{ display: "none" }} data-phase2="audio-visualiser" />

      <div className="pointer-events-none absolute -top-32 -right-32 h-72 w-72 rounded-full bg-[#ed4609]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-56 w-56 rounded-full bg-[#111133]/60 blur-3xl" />

      <div className="relative z-10">
        <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[#ed4609]" data-testid="avatar-eyebrow">
          The Revol Engine
        </p>
        <h1 className="mt-3 text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.02]">
          <MaskedLine delay={0.35}>Ask</MaskedLine>
          <MaskedLine delay={0.5} className="text-[#ed4609]">Revelle.</MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-3 max-w-sm text-sm text-[#a1a1aa] leading-relaxed"
        >
          Instant, grounded answers on tours, avatars, dubbing and more - straight from the Revol Studios knowledge base.
        </motion.p>
      </div>

      <div className="relative z-10 hidden flex-1 items-center justify-center py-8 lg:flex" style={{ perspective: 800 }}>
        <motion.div style={{ rotateX, rotateY }} className="relative flex items-center justify-center">
          <div className="orb-ring-outer absolute h-52 w-52 rounded-full border border-[#ed4609]/30" />
          <div className="orb-ring absolute h-40 w-40 rounded-full border-2 border-[#ed4609]/50" />
          <div className="orb-core h-28 w-28 rounded-full" data-testid="avatar-orb" />
        </motion.div>
      </div>

      <div className="relative z-10 mt-5 space-y-5 lg:mt-0">
        <div className="flex items-center gap-3">
          <div className="orb-core h-8 w-8 shrink-0 rounded-full lg:hidden" />
          <span className="text-lg font-semibold text-white" data-testid="avatar-name">Revelle</span>
          <span
            className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-[#a1a1aa]"
            data-testid="avatar-status"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${status === "ready" ? "bg-[#22c55e]" : "bg-[#ed4609]"} shadow-[0_0_6px_currentColor]`} />
            {statusLabel}
          </span>
          <span className="ml-auto hidden sm:flex items-center gap-1.5 rounded-full border border-[#ed4609]/25 bg-[#ed4609]/5 px-3 py-1 font-mono-brand text-[10px] uppercase tracking-widest text-[#ed4609]/80" data-testid="phase2-badge">
            <Orbit className="h-3 w-3" /> FAQ Intelligence Live
          </span>
        </div>

        <div className="hidden flex-wrap gap-2 lg:flex" data-testid="avatar-capabilities">
          {capabilities.map((c, i) => (
            <motion.span
              key={c}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 + i * 0.07 }}
              className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-[#a1a1aa]"
            >
              <Sparkles className="h-3 w-3 text-[#ed4609]/70" />
              {c}
            </motion.span>
          ))}
        </div>

        <a
          href={CALENDLY}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="avatar-calendly-link"
          className="group flex items-center justify-between rounded-xl border border-[#ed4609]/25 bg-[#ed4609]/5 px-4 py-3 text-sm text-white transition-colors duration-200 hover:border-[#ed4609]/60 hover:bg-[#ed4609]/15"
        >
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#ed4609]" />
            Prefer a human? Schedule a free strategy call
          </span>
          <ArrowUpRight className="h-4 w-4 text-[#ed4609] transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </motion.section>
  );
};
